import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';

type ApiEnvelope<T> = {
  Code?: number;
  Data?: T;
  Message?: string;
};

type RawFileStorageItem = {
  Id?: string;
  Name?: string;
  ContentType?: string;
  Length?: number;
  UpdatedAt?: string;
  Link?: string | null;
  ParentItemId?: string;
  IsFavorite?: boolean;
  ContentFragment?: string | null;
  Roles?: string[];
  HasSubDirectories?: boolean;
};

type RawFileStorageData = {
  FileItems?: RawFileStorageItem[];
  ParentId?: string;
  HaveParentId?: boolean;
};

export type FileStorageItem = {
  id: string;
  name: string;
  contentType: string;
  length: number;
  updatedAt: string;
  link: string | null;
  parentItemId: string | null;
  isFavorite: boolean;
  contentFragment: string | null;
  roles: string[];
  isFolder: boolean;
  isUrl: boolean;
  hasSubDirectories: boolean;
};

export type FileStorageListResponse = {
  fileItems: FileStorageItem[];
  parentId: string | null;
  haveParentId: boolean;
};

type FileManagerPathInfo = {
  Key?: string | null;
  key?: string | null;
  Name?: string;
  name?: string;
};

export type FileStoragePathSegment = {
  id: string;
  name: string;
};

type RawPathStackPayload = {
  Segments?: FileManagerPathInfo[];
};

type UploadChunkMetadata = {
  UploadId: string;
  FileName: string;
  Index: number;
  TotalCount: number;
  FileSize: number;
};

export type UploadFieldFileResponse = {
  success?: boolean;
  errorId?: string | null;
  errorText?: string | null;
  result?: string;
};

export type DirectoryInfo = {
  IsDirectory?: boolean;
  Key?: string;
  Name?: string;
  ParentPath?: string;
  Path?: string;
  PathInfo?: FileManagerPathInfo[];
  PathKeys?: string[];
};

const mapPathSegment = (raw: FileManagerPathInfo): FileStoragePathSegment => ({
  id: raw.Key ?? raw.key ?? '',
  name: raw.Name ?? raw.name ?? '',
});

const FILE_STORAGE_API = '/umbraco/api/FileStorage';
const FILE_MANAGER_API = '/umbraco/api/FileManager';
/** SPA file manager: no DevExtreme command processor */
const SPA_FILE_MANAGER_API = '/umbraco/api/SpaFileManager';

const DIRECTORY_CONTENT_TYPE = 'Directory';

const normalizeEnvelope = <T>(response: ApiEnvelope<T>): T => {
  const code = response.Code;
  if (code != null && code !== 1) {
    throw new Error(response.Message ?? 'Request failed');
  }
  const payload = response.Data as T | undefined;
  if (!payload) {
    throw new Error('Empty response payload');
  }
  return payload;
};

const mapItem = (raw: RawFileStorageItem): FileStorageItem => {
  const contentType = raw.ContentType ?? '';
  return {
    id: raw.Id ?? '',
    name: raw.Name ?? '',
    contentType,
    length: raw.Length ?? 0,
    updatedAt: raw.UpdatedAt ?? '',
    link: raw.Link ?? null,
    parentItemId: raw.ParentItemId ?? null,
    isFavorite: raw.IsFavorite ?? false,
    contentFragment: raw.ContentFragment ?? null,
    roles: raw.Roles ?? [],
    isFolder: contentType === DIRECTORY_CONTENT_TYPE,
    isUrl: contentType === 'Url',
    hasSubDirectories: raw.HasSubDirectories ?? false,
  };
};

const mapItemsPayload = (payload: RawFileStorageData): FileStorageListResponse => {
  const fileItems = (payload.FileItems ?? []).map(mapItem);
  const parentId = payload.ParentId ?? null;
  const haveParentId = payload.HaveParentId ?? false;
  return { fileItems, parentId, haveParentId };
};

export type DownloadFileResponse = {
  Name?: string;
  name?: string;
  Content?: string;
  content?: string;
  MimeType?: string;
  mimeType?: string;
};

type SearchPayload = {
  FileResults?: RawFileStorageItem[];
};

type CreateLinkFileRequest = {
  parentId?: string;
  path?: string;
  pathKeys?: string[];
  urlName: string;
  url: string;
};

export const fileStorageApi = createApi({
  reducerPath: 'fileStorageApi',
  baseQuery,
  tagTypes: ['FileStorage'],
  endpoints: (builder) => ({
    getFolderItems: builder.query<
      FileStorageListResponse,
      { nodeKey?: string; storageType: string }
    >({
      query: ({ nodeKey, storageType }) => ({
        url: `${FILE_STORAGE_API}/Get`,
        params: { nodeKey: nodeKey ?? '', storageType },
      }),
      transformResponse: (response: ApiEnvelope<RawFileStorageData>) => {
        const payload = normalizeEnvelope(response);
        return mapItemsPayload(payload);
      },
      providesTags: ['FileStorage'],
    }),

    getItemPath: builder.query<FileStoragePathSegment[], { itemId: string }>({
      query: ({ itemId }) => ({
        url: `${FILE_STORAGE_API}/GetItemPath`,
        params: { itemId },
      }),
      transformResponse: (response: ApiEnvelope<RawPathStackPayload>) => {
        const payload = normalizeEnvelope(response);
        return (payload.Segments ?? []).map(mapPathSegment).filter((segment) => segment.id);
      },
    }),

    getTrashItems: builder.query<FileStorageListResponse, void>({
      query: () => ({
        url: `${SPA_FILE_MANAGER_API}/GetTrash`,
      }),
      transformResponse: (response: ApiEnvelope<RawFileStorageData>) => {
        const payload = normalizeEnvelope(response);
        return mapItemsPayload(payload);
      },
      providesTags: ['FileStorage'],
    }),

    searchFiles: builder.query<FileStorageItem[], { query: string; storageType: string }>({
      query: ({ query, storageType }) => ({
        url: `${FILE_STORAGE_API}/GetFilesBySearch`,
        params: { query, storageType },
      }),
      transformResponse: (response: ApiEnvelope<SearchPayload>) => {
        const payload = normalizeEnvelope(response);
        const results = payload.FileResults ?? [];
        return results.map(mapItem);
      },
    }),

    getFoundFiles: builder.query<
      FileStorageListResponse,
      { fileKey: string; query: string; storageType: string }
    >({
      query: ({ fileKey, query, storageType }) => ({
        url: `${FILE_STORAGE_API}/GetFoundFiles`,
        params: { fileKey, query, storageType },
      }),
      transformResponse: (response: ApiEnvelope<RawFileStorageData>) => {
        const payload = normalizeEnvelope(response);
        return mapItemsPayload(payload);
      },
    }),

    appendToFavorite: builder.mutation<void, { fileId: string; status: boolean }>({
      query: ({ fileId, status }) => ({
        url: `${FILE_STORAGE_API}/AppendToFavorite`,
        method: 'POST',
        params: { fileId, status },
      }),
      invalidatesTags: ['FileStorage'],
    }),

    downloadFile: builder.mutation<DownloadFileResponse, { key: string }>({
      query: ({ key }) => ({
        url: `${FILE_STORAGE_API}/DownloadFile`,
        method: 'POST',
        params: { key },
      }),
    }),

    renameItem: builder.mutation<void, { id: string; newName: string }>({
      query: ({ id, newName }) => ({
        url: `${SPA_FILE_MANAGER_API}/RenameItem`,
        method: 'POST',
        params: { id, newName },
      }),
      invalidatesTags: ['FileStorage'],
    }),

    createDirectory: builder.mutation<void, { parentId?: string; name: string }>({
      query: ({ parentId, name }) => ({
        url: `${SPA_FILE_MANAGER_API}/CreateDirectory`,
        method: 'POST',
        params: { parentId: parentId ?? '', name },
      }),
      invalidatesTags: ['FileStorage'],
    }),

    uploadToFolder: builder.mutation<void, { parentId?: string; file: File }>({
      query: ({ parentId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${SPA_FILE_MANAGER_API}/UploadToFolder`,
          method: 'POST',
          params: { parentId: parentId ?? '' },
          body: formData,
        };
      },
      invalidatesTags: ['FileStorage'],
    }),

    getDirectory: builder.query<FileManagerPathInfo[], { dirName: string }>({
      query: ({ dirName }) => ({
        url: `${FILE_STORAGE_API}/GetDirectory`,
        params: { dirName },
      }),
      transformResponse: (response: DirectoryInfo) => {
        if (response.PathInfo && response.PathInfo.length > 0) {
          return response.PathInfo;
        }
        return [{ name: response.Name, key: response.Key }];
      },
    }),

    deleteFile: builder.mutation<void, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `${FILE_STORAGE_API}/Delete`,
        method: 'POST',
        params: { Id: fileId },
      }),
    }),

    uploadFieldFile: builder.mutation<
      string,
      { file: File; destinationPathInfo: FileManagerPathInfo[] }
    >({
      query: ({ file, destinationPathInfo }) => {
        const chunkMetadata: UploadChunkMetadata = {
          UploadId: crypto.randomUUID(),
          FileName: file.name,
          Index: 0,
          TotalCount: 1,
          FileSize: file.size,
        };

        const args = {
          destinationPathInfo,
          chunkMetadata: JSON.stringify(chunkMetadata),
        };

        const formData = new FormData();
        formData.append('chunk', file, 'blob');
        formData.append('arguments', JSON.stringify(args));
        formData.append('command', 'UploadChunk');

        return {
          url: `${FILE_MANAGER_API}/Upload`,
          method: 'POST',
          params: { returnId: true },
          body: formData,
        };
      },
      transformResponse: (response: UploadFieldFileResponse) => {
        if (response.success === false) {
          throw new Error(response.errorText || 'Upload failed');
        }
        if (!response.result) {
          throw new Error('Upload succeeded but no file id was returned');
        }
        return response.result;
      },
    }),

    viewFile: builder.query<Blob, { key: string }>({
      query: ({ key }) => ({
        url: `${FILE_STORAGE_API}/ViewFile`,
        params: { key },
        responseHandler: (response) => response.blob(),
      }),
    }),

    removeItem: builder.mutation<void, { pathInfo: FileManagerPathInfo[]; isDirectory: boolean }>({
      query: ({ pathInfo, isDirectory }) => ({
        url: `${SPA_FILE_MANAGER_API}/RemoveItem`,
        method: 'POST',
        body: { pathInfo, isDirectory },
      }),
      invalidatesTags: ['FileStorage'],
    }),

    moveItem: builder.mutation<
      void,
      {
        sourcePathInfo: FileManagerPathInfo[];
        sourceIsDirectory: boolean;
        destinationPathInfo: FileManagerPathInfo[];
      }
    >({
      query: (body) => ({
        url: `${SPA_FILE_MANAGER_API}/MoveItem`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FileStorage'],
    }),

    copyItem: builder.mutation<
      void,
      {
        sourcePathInfo: FileManagerPathInfo[];
        sourceIsDirectory: boolean;
        destinationPathInfo: FileManagerPathInfo[];
      }
    >({
      query: (body) => ({
        url: `${SPA_FILE_MANAGER_API}/CopyItem`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FileStorage'],
    }),

    createLinkFile: builder.mutation<void, CreateLinkFileRequest>({
      query: ({ parentId, path, pathKeys, urlName, url }) => ({
        url: `${SPA_FILE_MANAGER_API}/CreateLinkFile`,
        method: 'POST',
        body: {
          path: path ?? '',
          isDirectory: true,
          pathKeys: pathKeys ?? (parentId ? [parentId] : []),
          urlName,
          url,
        },
      }),
      invalidatesTags: ['FileStorage'],
    }),

    getRoles: builder.query<string[], void>({
      query: () => ({ url: `${SPA_FILE_MANAGER_API}/GetRoles` }),
      transformResponse: (response: { Data?: string[] }) => response.Data ?? [],
    }),

    changeRole: builder.mutation<void, { roles: string[]; filesKeys: string[] }>({
      query: ({ roles, filesKeys }) => ({
        url: `${SPA_FILE_MANAGER_API}/ChangeRole`,
        method: 'POST',
        body: { Roles: roles, FilesKeys: filesKeys },
      }),
      invalidatesTags: ['FileStorage'],
    }),

    createAccessLink: builder.mutation<
      { code: string; key: string },
      { fileKey: string; dateStart?: string; dateEnd?: string; description?: string }
    >({
      query: ({ fileKey, dateStart, dateEnd, description }) => ({
        url: `${SPA_FILE_MANAGER_API}/CreateAccessLink`,
        method: 'POST',
        body: {
          FileKey: fileKey,
          DateStart: dateStart,
          DateEnd: dateEnd,
          Description: description,
        },
      }),
      transformResponse: (
        response: ApiEnvelope<{
          Data?: { Code?: string; Key?: string };
        }>
      ) => {
        const outer = response.Data ?? {};
        const inner = (outer as { Data?: { Code?: string; Key?: string } }).Data ?? outer;
        return {
          code: (inner as { Code?: string }).Code ?? '',
          key: (inner as { Key?: string }).Key ?? '',
        };
      },
    }),
  }),
});

export const {
  useGetFolderItemsQuery,
  useGetTrashItemsQuery,
  useLazyGetItemPathQuery,
  useLazyGetFolderItemsQuery,
  useLazySearchFilesQuery,
  useLazyGetFoundFilesQuery,
  useAppendToFavoriteMutation,
  useDownloadFileMutation,
  useRenameItemMutation,
  useCreateDirectoryMutation,
  useUploadToFolderMutation,
  useRemoveItemMutation,
  useMoveItemMutation,
  useCopyItemMutation,
  useCreateLinkFileMutation,
  useGetRolesQuery,
  useChangeRoleMutation,
  useCreateAccessLinkMutation,
  useUploadFieldFileMutation,
  useLazyGetDirectoryQuery,
  useDeleteFileMutation,
  useLazyViewFileQuery,
} = fileStorageApi;
